import type { MaybeRefOrGetter } from 'vue'

import { canonicalPath } from '#shared/lib/route'
import { computed, toValue } from 'vue'

import {
  normalizePageSeoDescription,
  normalizePageSeoText,
  type PageSeoBaseOptions,
  type PageSeoType,
} from '~/composables/pageSeo'

type UsePageSeoOptions = PageSeoBaseOptions & {
  readonly siteName?: MaybeRefOrGetter<string | undefined>
  readonly locale?: MaybeRefOrGetter<string | undefined>
}

const DEFAULT_OG_IMAGE_PATH = '/images/og.webp'

export const usePageSeo = (options: UsePageSeoOptions) => {
  const route = useRoute()
  const siteOrigin = useSiteOrigin()

  const seoTitle = computed<string>(() => normalizePageSeoText(toValue(options.title)))
  const seoDescription = computed<string>(() =>
    normalizePageSeoDescription(toValue(options.description))
  )
  const canonicalUrl = computed<string>(() =>
    new URL(canonicalPath(route.path, route.query), siteOrigin.value).toString()
  )
  const seoType = computed<PageSeoType>(() => {
    const value = toValue(options.type)
    return value === 'article' ? 'article' : 'website'
  })
  const seoRobots = computed<string>(() => {
    const explicitRobots = normalizePageSeoText(toValue(options.robots))

    if (explicitRobots.length > 0) {
      return explicitRobots
    }

    return 'index,follow'
  })
  const seoSiteName = computed<string>(() =>
    normalizePageSeoText(toValue(options.siteName) || 'АКПП Центр')
  )
  const seoLocale = computed<string>(() => normalizePageSeoText(toValue(options.locale) || 'ru_RU'))
  const seoImage = computed<string>(() => {
    const rawValue = normalizePageSeoText(toValue(options.image) || DEFAULT_OG_IMAGE_PATH)

    return new URL(rawValue, siteOrigin.value).toString()
  })

  useHead(() => ({
    link: [
      {
        rel: 'canonical',
        href: canonicalUrl.value,
      },
    ],
  }))

  useSeoMeta({
    title: () => seoTitle.value,
    description: () => seoDescription.value,
    robots: () => seoRobots.value,
    ogTitle: () => seoTitle.value,
    ogDescription: () => seoDescription.value,
    ogUrl: () => canonicalUrl.value,
    ogType: () => seoType.value,
    ogSiteName: () => seoSiteName.value,
    ogLocale: () => seoLocale.value,
    ogImage: () => seoImage.value || undefined,
  })

  return {
    seoTitle,
    seoDescription,
    canonicalUrl,
    seoImage,
  }
}
